export async function put<T>(url: string, data: any = {}): Promise<T> {
    return makeCall(url, {
        method: 'PUT',
        body: data ? JSON.stringify(data) : null,
        headers: {
            'Content-Type': 'application/json'
        }
    }, false);
}

export async function patch<T>(url: string, data: any = {}): Promise<T> {
    return makeCall(url, {
        method: 'PATCH',
        body: data ? JSON.stringify(data) : null,
        headers: {
            'Content-Type': 'application/merge-patch+json'
        }
    }, false);
}

export async function postForm<T>(url: string, data: FormData, skipReloadOn401: boolean): Promise<T> {
    return makeCall(url, {
        method: 'POST',
        body: data,
        skipReloadOn401: skipReloadOn401
    }, false);
}

/**
 * Assembles a dictionary object into a GET parameter string. If the value of a key in the dictionary is an other dictionary, it is recursively traversed
 * using the initial key as a prefix to the found keys (using camelcase concatenation) in the child dictionary. All other items are stringified using toString().
 */
const assembleGetParameters = (data: any, prefix: string = ""): string =>
    Object
        .keys(data)
        .reduce(
            (acc, key) => {
                if(data[key] !== undefined && key.length > 0) {
                    const getKey = `${ prefix }${ prefix.length > 0 ? `${ key[0].toUpperCase() }${ key.slice(1) }` : key }`
                    const valIsObj = typeof data[key] === "object"
                    const addition = valIsObj ? assembleGetParameters(data[key], getKey) : data[key].toString()
                    if(addition.length > 0)
                        return acc + `${ acc.length > 0 ? "&" : "" }${ valIsObj ? addition : `${ getKey }=${ addition }` }`
                }
                return acc
            },
            ""
        )

export async function get<T>(url: string, data: any = {}, signal?: AbortSignal, jsonResponse: boolean = true, timeout: number = 15000): Promise<T> {
    const queryString = assembleGetParameters(data)
    const urlToCall = `${ url }${ queryString.length > 0 ? "?" : "" }${ queryString }`
    return makeCall(urlToCall, {
            method: 'GET',
            signal: signal,
        }, jsonResponse);
}


export async function makeCall<T>(url: string, options: any = {}, jsonResponse: boolean = true): Promise<T> {
    return new Promise<T>( (resolve, reject) => {
        const timerId = setTimeout(
            () => reject(new Error('Request timed out')), 15000
        );
        options.credentials = 'include';
        fetch(`${getApiUrl()}${url}`, options)
            .then(res => throwNon200(res, options.skipReloadOn401))
            .then(res => {
                const response = jsonResponse ? res.json() : {} as T;
                resolve(response);
            })
            .catch(err => reject(err))
            .finally(() => clearTimeout(timerId));
    }).catch(err => {
        throw handleError(err)
    });
}

function getApiUrl(): string {
    return process.env.REACT_APP_API_HOST || "";
}

function handleError(e: Error): Error {
    console.error(e);
    return e instanceof ApiError ? e : new ApiError("Failed to call SvD Access API");
}

async function throwNon200(res: Response, skipReloadOn401: boolean): Promise<Response> {
    if (res.ok) {
        return res;
    } else if (res.status === 401 && !skipReloadOn401) {
        window.localStorage.removeItem("svd-permissions");
        window.location.reload();
        throw new ApiError("Invalid credentials", res.status);
    } else {
        const errMsg = await getErrorMessage(res);
        throw new ApiError(errMsg, res.status);
    }
}

async function getErrorMessage(res: Response): Promise<string> {
    const errorMessage = `Server error ${res.status}`;
    return res.json()
        .then(json => json.error && json.error.message ? json.error.message : errorMessage)
        .catch(e => errorMessage)
}

class ApiError extends Error {
    statusCode: number | undefined;

    constructor(message?: string, statusCode?: number) {
        super(message);
        Object.setPrototypeOf(this, new.target.prototype);
        this.name = 'API error';
        this.statusCode = statusCode;
    }
}

